// Stripe is the source of truth for products + multi-currency Prices.
// my.tabletalk.app exposes a public, read-only endpoint that wraps Stripe so
// this marketing site never sees the secret key.
//
// Expected response shape (TBD — confirm with my.tabletalk.app):
//   {
//     plans: [
//       { id: "table",  name: "Table",  prices: { EUR: 79,  RON: 395, USD: 89  } },
//       { id: "room",   name: "Room",   prices: { EUR: 149, RON: 745, USD: 169 }, featured: true },
//       { id: "house",  name: "House",  prices: null }   // "Custom" plan
//     ]
//   }
//
// Currency selection here mirrors the locale chosen on the marketing site.
// The same {plan, currency} pair is forwarded to my.tabletalk.app on Sign Up
// (see lib/handoff.js) so what the user sees is what they're charged.

// Empty/undefined VITE_PRICING_ENDPOINT means "skip the network fetch and use
// the local fallback" — useful in dev before my.tabletalk.app/api/public/pricing
// is live.
const PRICING_ENDPOINT = import.meta.env.VITE_PRICING_ENDPOINT || null;

const LOCALE_TO_CURRENCY = {
  en: "EUR",
  ro: "RON",
  es: "EUR",
  fr: "EUR",
  de: "EUR",
  it: "EUR"
};

const FALLBACK_PRICING = {
  plans: [
    { id: "table", name: "Table", prices: { EUR: 79,  RON: 395, USD: 89  } },
    { id: "room",  name: "Location",  prices: { EUR: 149, RON: 745, USD: 169 }, featured: true },
    { id: "house", name: "House", prices: null }
  ]
};

let _cache = null;

export function currencyForLocale(locale) {
  return LOCALE_TO_CURRENCY[locale] || "EUR";
}

export async function getPricing() {
  if (_cache) return _cache;
  if (!PRICING_ENDPOINT) {
    _cache = FALLBACK_PRICING;
    return _cache;
  }
  try {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 3000);
    const res = await fetch(PRICING_ENDPOINT, { mode: "cors", signal: ctl.signal });
    clearTimeout(timer);
    if (!res.ok) throw new Error(`pricing HTTP ${res.status}`);
    _cache = await res.json();
    return _cache;
  } catch {
    _cache = FALLBACK_PRICING;
    return _cache;
  }
}

export function formatPrice(amount, currency, locale = "en") {
  if (amount == null) return "Custom";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}
